import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Mail, Lock, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || String(detail);
}

export default function Login() {
  const { user, setUser, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !accepted) { toast.error("Please agree to the Terms & Conditions and Privacy Policy to continue"); return; }
    setBusy(true);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const body = mode === "register"
        ? { ...form, remember }
        : { email: form.email, password: form.password, remember };
      const { data } = await api.post(path, body);
      setUser(data);
      toast.success(mode === "register" ? "Welcome to Vaelith" : "Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Authentication failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5">
      <div className="max-w-md w-full">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 font-mono-data text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-6">
            <ShieldCheck className="w-4 h-4" /> Secure Access
          </div>
          <h1 className="font-display text-4xl tracking-tighter font-medium">{mode === "register" ? "Create your account" : "Sign in to Vaelith"}</h1>
          <p className="text-muted-foreground mt-3 leading-relaxed">Build a watchlist, store searches, and receive verified market alerts.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <div className="relative">
              <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="auth-name" className="pl-9 h-11" />
            </div>
          )}
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="auth-email" className="pl-9 h-11" />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"} data-testid="auth-password" className="pl-9 h-11" />
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="remember-me-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="remember-me-checkbox"
              className="h-4 w-4 rounded border-border accent-accent cursor-pointer"
            />
            Keep me signed in
          </label>
          {mode === "register" && (
            <div className="space-y-2 pt-1">
              <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer select-none" data-testid="accept-terms-label">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="accept-terms-checkbox"
                  className="h-4 w-4 mt-0.5 rounded border-border accent-accent cursor-pointer shrink-0" />
                <span>I am 18+ and agree to the{" "}
                  <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-accent hover:underline">Terms &amp; Conditions</a> and{" "}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-accent hover:underline">Privacy Policy</a>.
                </span>
              </label>
              <p className="text-xs text-muted-foreground/80 leading-relaxed" data-testid="privacy-notice">
                Vaelith collects your name, email, and account authentication information to create and manage your account and provide the Service.
                See our <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-accent hover:underline">Privacy Policy</a> for details.
              </p>
            </div>
          )}
          <Button type="submit" disabled={busy || (mode === "register" && !accepted)} data-testid="auth-submit" className="rounded-full w-full h-11">
            {busy ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
          </Button>
        </form>

        <div className="text-center mt-4 text-sm text-muted-foreground">
          {mode === "login" ? (
            <>New to Vaelith? <button data-testid="switch-register" onClick={() => setMode("register")} className="text-accent hover:underline">Create an account</button></>
          ) : (
            <>Already have an account? <button data-testid="switch-login" onClick={() => setMode("login")} className="text-accent hover:underline">Sign in</button></>
          )}
        </div>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-border" />
          <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <Button onClick={login} variant="outline" data-testid="google-signin-button" className="rounded-full w-full h-11">
          Continue with Google
        </Button>
      </div>
    </div>
  );
}
